/* -------------------------------------------------------------------------- */
/* --------------------------- THEME : FRAMEWORKS --------------------------- */

// FRAMEWORKS : NODES_MODULES
import Swiper, { Autoplay, Navigation, Parallax, Scrollbar } from "swiper";

// FRAMEWORKS : GSAP BUSINESS
import "../js/gsap-business-green/gsap.min.js";
import "../js/gsap-business-green/EasePack.min.js";
import "../js/gsap-business-green/ScrollSmoother.min.js";
import "../js/gsap-business-green/ScrollTrigger.min.js";
import "../js/gsap-business-green/ScrollToPlugin.min.js";
import { SplitText } from "../js/gsap-business-green/SplitText.min.js";

/* -------------------------------------------------------------------------- */
/* ------------------------ THEME : GLOBAL VARIABLES ------------------------ */

// ScrollSmoother (GSAP)
let scrollSmooth = ScrollSmoother.create({
  wrapper: "#page-wrapper",
  content: "#page-main",
  smooth: 1,
  smoothTouch: false,
  effects: true,
  normalizeScroll: false,
  ignoreMobileResize: true,
});

// Élèment à animer (en CSS ou JS)
const elementsToAnimated = document.querySelectorAll([
  // global
  "[data-animation]",
  // elementor
  ".page__article .elementor-button-wrapper",
  ".page__article .elementor-heading-title",
  ".page__article .elementor-icon-list-item",
  ".page__article .elementor-icon-wrapper",
  ".page__article .elementor-sitemap-section > *",
  ".page__article .elementor-widget-html",
  ".page__article .elementor-widget-image",
  ".page__article .elementor-widget-nav-menu",
  ".page__article .elementor-widget-text-editor",
  ".page__article .elementor-widget-video",
  ".page__article .elementor-image-box-wrapper",
  ".page__article .elementor-shortcode",
]);
const textsToAnimated = document.querySelectorAll([
  // global
  "[data-animation='reveal-text']",
  ".page__article .elementor-heading-title",
]);

/* -------------------------------------------------------------------------- */
/* ------------------------- THEME : GLOBAL EVENTS -------------------------- */

// DOCUMENT READY :
document.addEventListener("DOMContentLoaded", () => {
  // remove 'no-js' body class
  document.body.classList.remove("no-js");

  // Preloader (et gestion des animations GSAP)
  gsapPreLoader();
  // Gestion des menus
  headerScrolledMenu(160);
  headerMobileMenu();
  menuItemDropdown();
  // Gestion de la bannière
  bannerClosing();
  bannerScrollingDetect();
  // Gestion des "data" custom
  popupInit();
  toggleInit();
  // Gestion des sliders
  allSwiperInit();
  // Gestion du cache
  sessionStorage.setItem("visited", true);
});

// DOCUMENT RESIZED
ScrollTrigger.addEventListener("refreshInit", () => {
  // Actualisation du smooth scroll
  gsapScrollSmootherState("refresh");
  // Actualisation de la bannière
  bannerScrollingDetect();
});

/* -------------------------------------------------------------------------- */
/* ----------------------- THEME : GLOBAL FUNCTIONS ------------------------- */

// Détection du scroll
function headerScrolledMenu(scrollOffset) {
  let position = window.pageYOffset;

  if (position > scrollOffset) {
    document.body.classList.add("page-scrolled");
  } else {
    document.body.classList.remove("page-scrolled");
  }

  document.addEventListener("scroll", () => {
    // detect scroll direction
    let scroll = window.pageYOffset;
    if (scroll > position) {
      // if scroll to bottom
      if (scroll < scrollOffset) {
        document.body.classList.remove("page-scrolled");
        document.body.classList.remove("page-scrolling");
      } else {
        document.body.classList.add("page-scrolling");
        document.body.classList.add("page-scrolled");
      }
    }
    // if scroll to top
    else {
      document.body.classList.remove("page-scrolling");
      if (scroll < scrollOffset) {
        document.body.classList.remove("page-scrolled");
      }
    }
    // if scroll max reached
    if (scroll + window.innerHeight >= document.documentElement.scrollHeight) {
      document.body.classList.add("page-scroll-completed");
    } else {
      document.body.classList.remove("page-scroll-completed");
    }
    position = scroll;
  });
}

// Menu Burger : Comportement au clic
function headerMobileMenu() {
  const pageHeaderBurger = document.querySelector(
    "#page-header #page-header-burger"
  );
  const pageHeader = document.querySelector("#page-header");

  pageHeaderBurger.addEventListener("click", () => {
    if (pageHeader.classList.contains("page__header--open")) {
      // Fermer le menu au clic sur le bouton burger
      document.documentElement.style.overflowY = "auto";
      if (scrollSmooth) {
        gsapScrollSmootherState("play");
      }
      pageHeader.classList.remove("page__header--open");
    } else {
      // Ouvrir le menu au clic sur le bouton burger
      document.documentElement.style.overflowY = "hidden";
      if (scrollSmooth) {
        gsapScrollSmootherState("pause");
      }

      pageHeader.classList.add("page__header--open");
    }
  });

  document.addEventListener("mouseup", (event) => {
    // Fermer le menu au clic dehors de la zone
    if (
      !pageHeader.contains(event.target) &&
      event.target !== pageHeader &&
      !event.target.closest("#page-header")
    ) {
      document.documentElement.style.overflowY = "auto";
      if (scrollSmooth) {
        gsapScrollSmootherState("play");
      }
      pageHeader.classList.remove("page__header--open");
    }
  });
}

// Menu Dropdown : Comportement au clic
function menuItemDropdown() {
  const menuItemLinks = document.querySelectorAll(
    '.menu .menu__item .menu__link[href="#"]'
  );

  menuItemLinks.forEach((item) => {
    item.addEventListener("click", (event) => {
      event.preventDefault();
      item.closest(".menu__item").classList.toggle("menu__item--active");
    });
  });

  document.addEventListener("mouseup", (event) => {
    const activeMenuItems = document.querySelectorAll(
      ".menu .menu__item.menu__item--active"
    );

    activeMenuItems.forEach((activeItem) => {
      if (
        !activeItem.isEqualNode(event.target) &&
        !activeItem.contains(event.target)
      ) {
        activeItem.classList.remove("menu__item--active");
      }
    });
  });
}

// Ouverture/Fermeture de la bannière
function bannerClosing() {
  const pageBanner = document.querySelector("#page-banner");
  const pageBannerClose = document.querySelector("#page-banner-close");

  if (pageBanner) {
    // bouton de fermeture
    pageBannerClose.addEventListener("click", () => {
      document.body.classList.remove("page-with-banner");
    });
  }
}

// Activation auto du défilement horizontal
function bannerScrollingDetect() {
  const pageBanner = document.querySelector("#page-banner");

  if (pageBanner) {
    const bannerwrapperWidth = pageBanner.offsetWidth;
    const bannerTextWidth =
      pageBanner.querySelector(".banner__text").offsetWidth;
    if (bannerwrapperWidth > bannerTextWidth + 60) {
      pageBanner.classList.remove("page__banner--scrolling");
    } else {
      pageBanner.classList.add("page__banner--scrolling");
    }
  }
}

// Popup : Comportement au clic
function popupInit() {
  const popups = document.querySelectorAll("[data-popup]");

  popups.forEach((popup) => {
    popup.addEventListener("click", (event) => {
      // event.preventDefault();

      // ouvrir/fermer la popup
      const thisPopupName = popup.dataset.popup;
      if (thisPopupName) {
        const thisPopupEl = document.querySelector(
          `.page__popup#${thisPopupName}`
        );
        thisPopupEl.classList.toggle("page__popup--open");
      }

      // mettre les vidéos des popups en pause
      const htmlVideoFromPopup =
        document.querySelectorAll(".page__popup video");
      if (htmlVideoFromPopup.length > 0) {
        htmlVideoFromPopup.forEach((video) => {
          video.pause();
        });
      }
    });
  });

  document.addEventListener("mouseup", (event) => {
    // si une popup est ouverte
    if (
      document.querySelectorAll(".page__popup.page__popup--open").length > 0
    ) {
      // click en dehors de la zone
      if (
        !document
          .querySelector(".page__popup .popup__wrapper")
          .contains(event.target) &&
        document.querySelector(".page__popup .popup__wrapper") !== event.target
      ) {
        // fermer la popup
        document.querySelectorAll(".page__popup").forEach((popup) => {
          popup.classList.remove("page__popup--open");
        });

        // mettre les vidéos des popups en pause
        const htmlVideoFromPopup =
          document.querySelectorAll(".page__popup video");
        if (htmlVideoFromPopup.length > 0) {
          htmlVideoFromPopup.forEach((popupVideo) => {
            popupVideo.pause();
          });
        }
      }
    }
  });
}

// Toggle : Comportement au clic
function toggleInit() {
  const toggleListener = document.querySelectorAll("[data-toggle]");

  toggleListener.forEach((toggleButton) => {
    toggleButton.addEventListener("click", (event) => {
      // ouvrir/fermer l'accordéon
      let toggleData = toggleButton.dataset.toggle;
      if (toggleData) {
        toggleData = toggleData.split(",");
        let toggleTarget = toggleData.shift();
        if (toggleTarget) {
          // cible
          let toggleTargetEl = document.querySelector(`#${toggleTarget}`);
          if (toggleData) {
            toggleData.forEach(function (className) {
              className = className.trim();
              // ajouter/retirer les classes renseignés
              toggleTargetEl.classList.toggle(className);
            });
          } else {
            // ajouter/retirer la classe ".--active"
            toggleTargetEl.classList.toggle("--active");
          }
        }
      }
    });
  });
}

// Activation de Swiper.js (sliders)
function allSwiperInit() {
  if (
    document.querySelectorAll(
      ".section-archive-related-news .swiper .swiper-slide"
    ).length > 0
  ) {
    const swiperRelatedNews = new Swiper(
      ".section-archive-related-news .swiper",
      {
        // Core parameters
        slidesPerView: "auto",
        speed: 1200,
        resizeObserver: true,
        // Modules
        modules: [Autoplay, Scrollbar],
        // Modules parameters
        autoplay: {
          delay: 3600,
          disableOnInteraction: true,
        },
        scrollbar: {
          el: ".section-archive-related-news .swiper-scrollbar",
          draggable: true,
        },
      }
    );
  }

  if (
    document.querySelectorAll(
      ".section-hero-single-portfolio .swiper .swiper-slide"
    ).length > 0
  ) {
    const swiperHeroSinglePortfolio = new Swiper(
      ".section-hero-single-portfolio .swiper",
      {
        // Core parameters
        slidesPerView: "auto",
        speed: 1200,
        loop: true,
        resizeObserver: true,
        // Modules
        modules: [Autoplay, Navigation, Parallax],
        // Modules parameters
        autoplay: {
          delay: 2400,
          disableOnInteraction: true,
        },
        navigation: {
          nextEl: ".swiper-button-next",
          prevEl: ".swiper-button-prev",
        },
        parallax: true,
      }
    );
  }
}

// État du scrollSmoother plugin
function gsapScrollSmootherState(state) {
  if (scrollSmooth) {
    // Désactiver sur Élémentor
    if (document.querySelector("body.elementor-editor-active")) {
      scrollSmooth.kill();
    }
    // Rafraîchir sinon
    else {
      if (state === "refresh") {
        scrollSmooth.refresh();
      }
      // Mettre en pause
      if (state === "pause") {
        scrollSmooth.paused(true);
      }
      if (state === "play" && scrollSmooth.paused()) {
        scrollSmooth.paused(false);
      }
    }
  }
}

// Scroll vers une ancre
function gsapScrollTo() {
  // avec un [href="#..."]
  const scrollToAnchor = document.querySelectorAll("a[href^='#']");
  scrollToAnchor.forEach((link) => {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      let anchorHref = link.getAttribute("href");
      scrollSmooth.scrollTo(anchorHref, true);
    });
  });

  // avec un [data-scrollto]
  const scrollToData = document.querySelectorAll("[data-scrollto]");
  scrollToData.forEach((data) => {
    data.addEventListener("click", () => {
      let anchorData = data.getAttribute("data-scrollto");
      if (anchorData == "auto") {
        anchorData = window.innerHeight + window.scrollY;
      }
      scrollSmooth.scrollTo(anchorData, true);
    });
  });

  // au chargement
  let hashInUrl = window.location.hash;
  if (hashInUrl) {
    scrollSmooth.scrollTo(hashInUrl, true);
  }
}

// Sticky element
function gsapStickyElement() {
  const stickyWrapper = document.querySelector("[data-sticky]");
  const stickyElementorWrapper = document.querySelector(
    "[data-sticky-elementor]"
  );

  if (stickyWrapper) {
    ScrollTrigger.matchMedia({
      // desktop
      "(min-width: 992px)": () => {
        ScrollTrigger.create({
          trigger: stickyWrapper.firstElementChild,
          start: "center center",
          end: () =>
            `+=${
              stickyWrapper.offsetHeight -
              stickyWrapper.firstElementChild.offsetHeight
            }`,
          pin: true,
          pinSpacing: false,
          invalidateOnRefresh: true,
          // markers: true,
        });
      },
    });
  }

  if (stickyElementorWrapper) {
    ScrollTrigger.matchMedia({
      // desktop
      "(min-width: 992px)": () => {
        ScrollTrigger.create({
          trigger: stickyElementorWrapper.firstElementChild.firstElementChild,
          start: "center center",
          end: () =>
            `+=${
              stickyElementorWrapper.offsetHeight -
              stickyElementorWrapper.firstElementChild.firstElementChild
                .offsetHeight
            }`,
          pin: true,
          pinSpacing: false,
          invalidateOnRefresh: true,
          // markers: true,
        });
      },
    });
  }
}

// Découpage des textes avec SplitText
function gsapSplitText() {
  textsToAnimated.forEach((_thisText) => {
    new SplitText(_thisText, {
      type: "lines, words", //, chars",
      linesClass: "line",
      wordsClass: "word",
      // charsClass: "letter",
    });
  });
}

// Animations avec le ScrollTrigger
function gsapAnimations() {
  // Ajout/retrait de la classe --animated
  elementsToAnimated.forEach((_this) => {
    let delay = _this.getAttribute("data-animation-delay")
      ? parseInt(_this.getAttribute("data-animation-delay"), 10)
      : 0;
    ScrollTrigger.create({
      trigger: _this,
      start: "-=100px +=100%",
      end: "bottom +=0%",
      onEnter: () => {
        setTimeout(() => {
          _this.classList.add("--animated");
        }, delay);
      },
      onLeaveBack: () => {
        _this.classList.remove("--animated");
      },
      // markers: true,
    });
  });

  // Animations sur la #timeline horizontal
  if (document.querySelector("#timeline")) {
    // Horizontal scroll
    let horizontalWrapper = document.querySelector(
      "#timeline #timeline-content > .elementor-widget-wrap"
    );
    let horizontalInertiaFactor = 2;
    let horizontalScroll = gsap.to(horizontalWrapper, {
      x: () =>
        -1 *
          (horizontalWrapper.scrollWidth -
            document.documentElement.clientWidth) +
        "px",
      ease: "none",
    });

    function timelineEnter() {
      gsap.to(".page__wrapper", {
        backgroundColor: gsap.getProperty("html", "--color-background-dark"),
      });
      gsap.to("#timeline .elementor-image-box-title", {
        color: gsap.getProperty("html", "--color-primary"),
      });
    }
    function timelineLeave() {
      gsap.to(".page__wrapper", {
        backgroundColor: gsap.getProperty("html", "--color-light"),
      });
      gsap.to("#timeline .elementor-image-box-title", {
        color: gsap.getProperty("html", "--color-text"),
      });
    }
    ScrollTrigger.create({
      trigger: horizontalWrapper,
      start: "center center",
      end: () => "+=" + horizontalWrapper.offsetWidth * horizontalInertiaFactor,
      scrub: 1,
      pin: true,
      pinSpacing: true,
      invalidateOnRefresh: true,
      animation: horizontalScroll,
      // markers: true,
      onEnter: () => timelineEnter(),
      onEnterBack: () => timelineEnter(),
      onLeave: () => timelineLeave(),
      onLeaveBack: () => timelineLeave()
    });

  }
}

// Loader d'accueil
function gsapPreLoader() {
  // Chargement des fonctions GSAP préalables
  beforePreloader();

  // Lancement de l'animation de chargement
  let loaderTimeline = gsap.timeline();
  // Première visite
  if (
    !sessionStorage.getItem("visited") ||
    document.querySelector("body.home")
  ) {
    loaderTimeline
      .to(window, {
        scrollTo: 0,
        duration: 0,
      })
      .call(gsapScrollSmootherState, ["pause"])
      .to("#page-preloader-content", {
        delay: 0.3,
        duration: 0.6,
        ease: "power1.out",
        opacity: 1,
      })
      .fromTo(
        "#page-preloader-content #logo-path-gradient",
        {
          opacity: 0,
          transform: "translate(-40px, 0)",
        },
        {
          duration: 0.6,
          ease: "power2.out",
          opacity: 1,
          transform: "translate(0, 0)",
        }
      )
      .fromTo(
        "#page-preloader-content #logo-path-reato",
        {
          opacity: 0,
          transform: "translate(20px, 0)",
        },
        {
          duration: 0.6,
          ease: "power2.out",
          opacity: 1,
          transform: "translate(0, 0)",
        },
        "<.1"
      )
      .fromTo(
        "#page-preloader-content #logo-path-construction",
        {
          opacity: 0,
          transform: "translate(20px, 0)",
        },
        {
          duration: 0.6,
          ease: "power2.out",
          opacity: 1,
          transform: "translate(0, 0)",
        },
        "<.2"
      )
      .to("#page-preloader-wrapper", {
        delay: 0.3,
        duration: 0.6,
        ease: "power2.out",
        opacity: 0,
      })
      .to("#page-preloader", {
        visibility: "hidden",
      });
    // after
    loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.5);
  }
  // Autres
  else {
    loaderTimeline
      // animation
      .to("#page-preloader-wrapper", {
        delay: 0.3,
        duration: 0.6,
        ease: "power2.out",
        opacity: 0,
      })
      .set("#page-preloader", {
        visibility: "hidden",
      });
    // after
    loaderTimeline.add(afterPreloader, loaderTimeline.duration() - 0.2);
  }
}

// Actions avant le Loader d'accueil
function beforePreloader() {
  // Chargement des fonctions GSAP préalables
  gsapSplitText();
  gsapStickyElement();
}

// Actions après le Loader d'accueil
function afterPreloader() {
  // Chargement des fonctions GSAP résultantes
  gsapScrollSmootherState("play");
  gsapAnimations();
  gsapScrollTo();
}
